/* eslint-disable max-lines */
import { Injectable, Injector } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { BaseFormService } from '@shared/services/base/base.form.service';
import { XprtDataService } from '@shared/services/xprt-data/xprt-data.service';
import { HawkerProfileService } from '@shared/services/hawker-profile/hawker-profile.service';
import { XdmCommissionRateService } from '@shared/services/xdm-commission-rate/xdm-commission-rate.service';
import {
  CreateFormConfig,
  CreateFormRules,
  EntityId,
  ENTITY_TYPES,
  GridRowData,
  IApiListResponse,
  IBaseEntityType,
  IBaseFormService,
  ICommissionRate,
  IEmployee,
  IEvent,
  IProduct,
  ROW_SELECTOR_ID,
  SelectOption,
  XuxGridAction,
  XuxGridActionPayload,
} from '@shared/types';
import {
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import { createFormRules as createHawkerFormRules } from './form-rules';
import { createRightFormConfig } from './right-form.form-config';
import {
  assignIn,
  chain,
  difference,
  filter,
  find,
  get,
  head,
  includes,
  intersection,
  invoke,
  isEmpty,
  isEqual,
  isNil,
  map,
  noop,
  omit,
  partition,
  toLower,
  uniqBy,
} from 'lodash';
import { lazyInject } from '@containers/base/decorators';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  observableOf,
  rx,
} from '@app/shared/rx';
import { BohDataService } from '../boh-data/boh-data.service';
import { I18n } from '@app/i18n/strings-keys';
import { ProductService } from '../product/product.service';
import { HawkerLoadService } from '../hawker-load';
import {
  DEFAULT_LOAD,
  MAX_PAGE_SIZE,
  TIME_FORMATS,
} from '@config/global-constants';
import { fromPromise } from 'rxjs/internal-compatibility';
import { NumberFormatService } from '../number-format/number-format.service';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { calcQtyRequested, formatEmployeeName } from './hawker-session.utils';
import { CompanyUtilService } from '../company';
import { formatEventStartAndEndDateTime } from '@app/shared/utils/event.utils';
import { ValidationService } from '@app/components/form/services/validation/validation.service';
import { DataSource } from '../datasource/entity-data-source.service';
import { decimalNumber } from '@app/shared/common';
import {
  ColDef,
  ColumnApi,
  GridApi,
  ValueFormatterParams,
} from 'ag-grid-community';
import {
  IHawkerSession,
  IHawkerSessionFormFieldPath,
} from '@app/shared/types/entities/hawker-session/hawker-session.types';
import {
  DEFAULT_INFORMATION_DATA,
  HAWKER_SESSION_FORM_FIELD_PATH,
} from './hawker-session.constant';
import { HAWKER_SESSION_STATUS_VALUE } from '@app/shared/types/entities/hawker-session/hawker-session-status.enum';
import { sortData } from '@app/shared/utils/common-utils';
import { HawkerSessionEditContainerComponent } from '@containers/hawker-session/containers';
import { HawkerSessionService } from './hawker-session.service';
import { APIActions } from '@app/store/actions/api-actions/api.actions';
import { unsavedWhenSwitchModalOptions } from '@app/containers/hawker-session/containers/edit/unsaved.modal.definition';
import { UiActions } from '@app/store/actions';
import { BTN_YES } from '@app/text-content/button-templates/button-templates-content';
import { buildSiteNameWithNumber } from '@app/shared/utils';

@Injectable()
export class HawkerSessionFormService
  extends BaseFormService
  implements IBaseFormService<ENTITY_TYPES.HAWKER_SESSION>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.HAWKER_SESSION;

  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(ValidationService) public validationService: ValidationService;

  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(HawkerProfileService)
  public hawkerProfileService: HawkerProfileService;
  @lazyInject(XdmCommissionRateService)
  public xdmCommissionRateService: XdmCommissionRateService;
  @lazyInject(BohDataService) public bohDataService: BohDataService;
  @lazyInject(ProductService) public productService: ProductService;
  @lazyInject(HawkerLoadService) public hawkerLoadService: HawkerLoadService;
  @lazyInject(HawkerSessionService)
  public hawkerSessionService: HawkerSessionService;

  public xuxGrid: XuxEditGridComponent;
  public gridApi: GridApi;

  public addProductXuxGrid: XuxEditGridComponent;
  public addProductGridApi: GridApi;

  public hawkerSessions: IHawkerSession[] = [];

  /**
   * Commission rates of the currently selected hawker (or the snapshot rate for a canceled session).
   * Sourced from hawker profile / document snapshot; consumed by `commissionRateDataSource.fetchList`
   * and the commission-rate change subscription.
   */
  private commissionRates: ICommissionRate[] = [];

  private readonly sessionColumnsToHide: string[] = [
    'load_requested',
    'load',
    'requested',
    'returned',
  ];

  private readonly closedSessionColumnsToShow: string[] = [
    'terminal_quantity',
    'variance_qty',
    'terminal_sales',
  ];

  public createFormRulesFunction: CreateFormRules<HawkerSessionFormService> =
    createHawkerFormRules;
  public createRightFormConfigFunction: CreateFormConfig<HawkerSessionFormService> =
    createRightFormConfig;

  public get formKeys(): Record<'main' | string, string> {
    return {
      ...super.formKeys,
      addProduct: 'AddHawkerSessionProductForm',
    };
  }

  public formFieldPath: IHawkerSessionFormFieldPath =
    HAWKER_SESSION_FORM_FIELD_PATH;

  public rightFormFieldPath = {
    products: 'product_list',
  };

  public isRequestingProducts$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  /**
   * Placeholder shown inside the commission-rate entity_select input.
   * Written to a non-empty value when the selected hawker has no available
   * commission rate (see `populateCommissionRateForHawker`); reset to '' when
   * the hawker changes or the form is destroyed.
   */
  public selectedCommissionRatePlaceholder$: BehaviorSubject<string> =
    new BehaviorSubject('');

  public shouldEmitEmployee = true;

  public hawkerIdDataSource: DataSource;
  public commissionRateDataSource: DataSource;
  /**
   * The last employee ID for which data was fetched last time
   *
   * Use this to handle rollback for New Status Switch(A -> B -> C, and C should rollback to B not A)
   *
   */
  public lastFetchDataEmployeeId: EntityId;

  public hawkerOptions: IEmployee[] = [];

  public hawkerOptions$: BehaviorSubject<SelectOption[]> = new BehaviorSubject(
    [],
  );

  public isFormDisabled$ = new BehaviorSubject(false);

  public get enableRightExpand(): boolean {
    return true;
  }

  public get selectedSiteId(): string {
    return (
      this.getFormFieldValue(this.formFieldPath.siteId) ??
      get(this.route.snapshot.queryParams, 'site_id', '')
    );
  }

  public get selectedEventId(): string {
    return (
      this.getFormFieldValue(this.formFieldPath.eventId) ??
      get(this.route.snapshot.queryParams, 'event_id', '')
    );
  }

  public get initialEmployeeId(): EntityId {
    return get(history, ['state', 'initialEmployeeId'], '');
  }

  public get isHawkerTouched(): boolean {
    return this.getFormFieldControl(this.formFieldPath.employeeId)?.touched;
  }

  public get isCommissionRateTouched(): boolean {
    return this.getFormFieldControl(this.formFieldPath.commissionRateEntityId)
      ?.touched;
  }

  public get isCanceled(): boolean {
    return this.isSessionStatus(HAWKER_SESSION_STATUS_VALUE.Canceled);
  }

  public get isClosed(): boolean {
    return this.isSessionStatus(HAWKER_SESSION_STATUS_VALUE.Closed);
  }

  private isSessionStatus(status: HAWKER_SESSION_STATUS_VALUE): boolean {
    if (!this.form) {
      return false;
    }

    return getFormFieldValue(this.form, this.formFieldPath.status) === status;
  }

  public get employeeIdForSave(): EntityId {
    return this.isNewMode()
      ? this.lastFetchDataEmployeeId ?? this.initialEmployeeId
      : get(this.document, 'employee.id', '');
  }

  public isUseSnapshotData(): boolean {
    return this.isCanceled || this.isClosed;
  }

  public readonly defaultGeneralHeight = '492px';
  public readonly generalWithInfoHeight = '614px';
  public defaultGridHeight = `calc(100vh - ${this.defaultGeneralHeight})`;
  public gridHeight$: BehaviorSubject<string> = new BehaviorSubject(
    this.defaultGridHeight,
  );

  public onFormReady(form: FormGroup): void {
    this.enableRightExpand$.next(!this.isUseSnapshotData());

    this.populateEventInfo(form);
    this.populateSelectedSiteInfo(form);

    this.initHawkerIdSubscription(form);
    this.initCommissionRateSubscription(form);

    this.populateEmployeeInfo(form);

    if (this.isUseSnapshotData()) {
      this.populateProductsFromDocument(form);
    }
  }

  public initHawkerIdDataSource(): void {
    this.hawkerIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.HAWKER_PROFILE,
    );

    this.hawkerIdDataSource.fetchList = (): Promise<IApiListResponse> => {
      if (this.isUseSnapshotData()) {
        return this.buildHawkerOptionsFromDocument();
      }

      return this.fetchHawkerOptionsForSite(this.selectedSiteId);
    };
  }

  public initCommissionRateDataSource(): void {
    this.commissionRateDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.HAWKER_PROFILE,
    );

    this.commissionRateDataSource.fetchList = (): Promise<IApiListResponse> => {
      const rates: ICommissionRate[] = this.commissionRates;
      const primarySuffix = this.i18nService.translate(
        I18n.Common.PrimaryBrackets,
      );

      return Promise.resolve({
        // Pre-render the display name (with optional "(Primary)" suffix) here
        // so the BaseService default `getItemText` (which returns `name`) is
        // sufficient for both the dropdown and the selected-value input — same
        // pattern as `buildHawkerOptionsFromDocument` baking the badge into
        // the employee name.
        items: map(rates, (rate) => ({
          name:
            !isEmpty(rate.name) && rate?.is_primary
              ? `${rate.name} ${primarySuffix}`
              : rate.name,
          entity_id: rate.entity_id,
          is_primary: rate.is_primary,
        })),
        page_size: rates.length,
        total: rates.length,
        page_count: 1,
        current_page: 1,
      });
    };
  }

  public buildHawkerOptionsFromDocument(): Promise<IApiListResponse> {
    const employee = get(this.document, 'employee', {});
    const employeeId = get(employee, 'id', '');
    const fullName = formatEmployeeName(
      {
        Firstname: get(employee, 'first_name', ''),
        Lastname: get(employee, 'last_name', ''),
        BadgeNumber: get(employee, 'badge_number', ''),
      },
      { showBadgeNumber: true },
    );

    const hawker = { name: fullName, entity_id: employeeId };

    return Promise.resolve({
      items: [hawker],
      page_size: 1,
      total: 1,
      page_count: 1,
      current_page: 1,
    });
  }

  public initHawkerIdSubscription(form: FormGroup): void {
    const subscription = this.fieldValueChanges(this.formFieldPath.employeeId)
      .pipe(
        rx.distinctUntilChanged(),
        rx.filter(this.filterByShouldEmitEmployee.bind(this)),
        rx.switchMap(this.handleAnotherOpenSession.bind(this)),
        rx.switchMap(this.handleOpenNewPage.bind(this)),
        rx.switchMap((employeeId) => {
          this.lastFetchDataEmployeeId = employeeId;

          if (this.isUseSnapshotData()) {
            return this.populateCommissionRateFromDocument(form);
          }

          this.clearCommissionRateAndProducts(form);

          if (isEmpty(employeeId)) {
            return observableOf(null);
          }

          this.isRequestingProducts$.next(true);

          return fromPromise(
            this.populateCommissionRateForHawker(form, employeeId),
          ).pipe(
            rx.switchMap((commissionRate) => {
              if (isEmpty(commissionRate)) {
                return observableOf(null);
              }

              return this.fetchProductsAndUpdateRequestList(
                form,
                get(commissionRate, 'entity_id'),
              );
            }),
            rx.finalize(() => {
              this.isRequestingProducts$.next(false);
            }),
          );
        }),
      )
      .subscribe(noop);

    this.subscriptions.push(subscription);
  }

  public initCommissionRateSubscription(form: FormGroup): void {
    const subscription = this.fieldValueChanges(
      this.formFieldPath.commissionRateEntityId,
    )
      .pipe(
        rx.skip(1), // skip initial startWith emit, handled by populateCommissionRateForHawker
        rx.distinctUntilChanged(),
        rx.filter(
          () => !this.isUseSnapshotData() && this.isCommissionRateTouched,
        ),
        rx.switchMap((commissionRateEntityId) => {
          if (isEmpty(commissionRateEntityId)) {
            return observableOf(null);
          }

          const selectedRate = find(this.commissionRates, {
            entity_id: commissionRateEntityId,
          });

          this.clearCommissionRateAndProducts(form, {
            skipCommissionRate: true,
          });

          if (!selectedRate) {
            return observableOf(null);
          }

          this.isRequestingProducts$.next(true);

          return this.fetchProductList(commissionRateEntityId).pipe(
            rx.tap((products) =>
              this.updateRequestListWithSaved({
                form,
                products,
                savedProducts: this.getFormFieldValue(
                  this.formFieldPath.virtualProducts,
                ) as IProduct[],
              }),
            ),
            rx.finalize(() => {
              this.isRequestingProducts$.next(false);
            }),
          );
        }),
      )
      .subscribe(noop);

    this.subscriptions.push(subscription);
  }

  public filterByShouldEmitEmployee(): boolean {
    if (!this.shouldEmitEmployee) {
      // reset to default value for next time
      this.shouldEmitEmployee = true;

      return false;
    }

    return true;
  }

  public clearHawkerChangeState(): void {
    const employeeIdControl = this.getFormFieldControl(
      this.formFieldPath.employeeId,
    );
    employeeIdControl?.markAsUntouched();
    employeeIdControl?.markAsPristine();

    const commissionRateControl = this.getFormFieldControl(
      this.formFieldPath.commissionRateEntityId,
    );
    commissionRateControl?.markAsUntouched();
    commissionRateControl?.markAsPristine();
  }

  public openNewEditorBySession(
    employeeId: EntityId,
    options?: { forceNavigateWhenNew?: boolean; onlyHawkerChanged?: boolean },
  ): boolean {
    const { forceNavigateWhenNew = false, onlyHawkerChanged = false } =
      options ?? {};

    const hawkerSession = find(this.hawkerSessions, {
      employee: { id: employeeId },
    });

    if (!isNil(hawkerSession)) {
      if (onlyHawkerChanged) {
        this.clearHawkerChangeState();
      } else {
        this.form.markAsPristine();
      }

      this.apiActions.openDocumentEditor(
        this.entityConfig.ENTITY_TYPE,
        get(hawkerSession, '_id', ''),
        {
          forceReload: true,
        },
      );

      return true;
    }

    if (!this.isNewMode() || forceNavigateWhenNew) {
      if (onlyHawkerChanged) {
        this.clearHawkerChangeState();
      } else {
        this.form.markAsPristine();
      }

      this.apiActions.openDocumentEditor(
        this.entityConfig.ENTITY_TYPE,
        'add',
        {
          forceReload: true,
          state: {
            initialEmployeeId: employeeId,
          },
        },
        {
          site_id: this.selectedSiteId,
          event_id: this.selectedEventId,
        },
      );

      return true;
    }

    // not open new page, need reset products when hawker changed in new status
    if (this.isNewMode()) {
      const productsControl = this.getFormFieldControl(
        this.formFieldPath.products,
      );
      productsControl?.markAsUntouched();
      productsControl?.markAsPristine();
    }

    return false;
  }

  public async onBeforeUnsavedModalClose(
    employeeId: EntityId,
    confirmed: boolean,
    modalValue: { needToSave: boolean },
  ): Promise<boolean> {
    if (!confirmed) {
      // cancel rollback changes
      this.shouldEmitEmployee = false;

      setFormFieldValue(
        this.form,
        this.formFieldPath.employeeId,
        this.employeeIdForSave,
        false,
      );

      return true;
    }

    const needToSave = get(modalValue, 'needToSave', false);

    if (needToSave) {
      const saveResult = await this.editor.submitBtnClick(
        get(this.editor, 'document', {}),
        {
          formValueGetter: ({ form }) => {
            return assignIn({}, form.value, {
              employee: {
                id: this.employeeIdForSave,
              },
            });
          },
        },
      );

      if (isEmpty(saveResult?._id)) {
        // save failed, do not continue to close

        return false;
      }

      this.openNewEditorBySession(employeeId, {
        forceNavigateWhenNew: true,
      });

      return true;
    } else {
      const forceNavigateWhenNew = !this.isNewMode();

      this.openNewEditorBySession(employeeId, {
        forceNavigateWhenNew,
      });
    }

    return true;
  }

  public handleAnotherOpenSession(employeeId: EntityId): Observable<EntityId> {
    if (isEmpty(employeeId) || this.isUseSnapshotData()) {
      return observableOf(employeeId);
    }

    if (isEqual(get(this.document, 'employee.id'), employeeId)) {
      return observableOf(employeeId);
    }

    return observableOf(false).pipe(
      rx.switchMap(async () => {
        const openSession = find(this.hawkerSessions, {
          employee: { id: employeeId },
        });

        if (isEmpty(openSession)) {
          return true;
        }

        const isSameSiteAndEvent =
          isEqual(get(openSession, 'site_id'), this.selectedSiteId) &&
          isEqual(get(openSession, 'event.id'), this.selectedEventId);

        if (!isSameSiteAndEvent) {
          // have another open session but not in the same site and event, show warning and not allow to switch
          await this.uiActions.confirm({
            title: I18n.Common.Notice,
            text: this.i18nService.t(
              I18n.Forms.HawkerSessionForm.HawkerHasAnotherOpenSession,
              {
                siteName: buildSiteNameWithNumber({
                  site_name: get(openSession, 'site_name', ''),
                  store_number: get(openSession, 'site_store_number', ''),
                }),
                eventName: get(openSession, 'event.name', ''),
              },
            ),
            buttons: [{ ...BTN_YES, caption: I18n.Common.Ok }],
          });

          // cancel rollback changes
          this.shouldEmitEmployee = false;

          setFormFieldValue(
            this.form,
            this.formFieldPath.employeeId,
            this.employeeIdForSave,
            false,
          );

          return false;
        }

        return true;
      }),
      rx.filter((shouldEmit) => shouldEmit),
      rx.map(() => employeeId),
    );
  }

  public handleOpenNewPage(employeeId: EntityId): Observable<EntityId> {
    if (this.isUseSnapshotData()) {
      return observableOf(employeeId);
    }

    if (isEqual(get(this.document, 'employee.id'), employeeId)) {
      return observableOf(employeeId);
    }

    return observableOf(false).pipe(
      rx.switchMap(async () => {
        const dirtyFields =
          (invoke(this.editor, 'getDirtyFieldsPath') as string[]) ?? [];
        const onlyHawkerChanged = isEmpty(
          difference(dirtyFields, [
            this.formFieldPath.employeeId,
            this.formFieldPath.commissionRateEntityId,
          ]),
        );

        if (!onlyHawkerChanged && this.form.dirty) {
          const confirmedResult = await this.uiActions.promptFormModal({
            ...unsavedWhenSwitchModalOptions({
              i18nService: this.i18nService,
            }),
            beforeFormClose: this.onBeforeUnsavedModalClose.bind(
              this,
              employeeId,
            ),
          });

          const isConfirmed = get(confirmedResult, 'confirmed', false);

          // only return true when continue but not save in new status
          return (
            isConfirmed &&
            !get(confirmedResult, 'value.needToSave', false) &&
            this.isNewMode()
          );
        }

        return !this.openNewEditorBySession(employeeId, {
          onlyHawkerChanged,
        });
      }),
      rx.filter((shouldEmit) => shouldEmit),
      rx.map(() => employeeId),
    );
  }

  /**
   * clear commission rate related fields and products when hawker is changed
   *
   * @param form
   * @returns
   */
  public clearCommissionRateAndProducts(
    form: FormGroup,
    options?: {
      skipCommissionRate?: boolean;
    },
  ): void {
    const { skipCommissionRate = false } = options ?? {};
    if (!form || !(this.isHawkerTouched || this.isCommissionRateTouched)) {
      return;
    }

    // Reset placeholder when hawker changes so a stale "No available commission
    // rate" message from a previous hawker is not shown.
    this.selectedCommissionRatePlaceholder$.next('');

    if (!skipCommissionRate) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.commissionRateEntityId,
        null,
        false,
      );
    }

    // commission_rate.entity_id is now the widget binding; reset its touched
    // state so the change subscription does not refire on the cleared value.
    this.getFormFieldControl(
      this.formFieldPath.commissionRateEntityId,
    )?.markAsUntouched();

    setFormFieldValue(this.form, this.formFieldPath.products, [], false);

    // also right add product list form
    setFormFieldValue(
      this.getForm(this.formKeys.addProduct),
      this.rightFormFieldPath.products,
      [],
      false,
    );
  }

  public setNoAvailableCommissionRate(_: FormGroup): void {
    this.selectedCommissionRatePlaceholder$.next(
      this.i18nService.translate(
        I18n.Forms.HawkerSessionForm.NoAvailableCommissionRate,
      ),
    );
  }

  public populateCommissionRateFromDocument(form: FormGroup): Observable<null> {
    const commissionRateEntityId = get(
      this.document,
      'commission_rate.entity_id',
      '',
    );
    const commissionRateName = get(this.document, 'commission_rate.name', '');
    const commissionRateIsPrimary = get(
      this.document,
      'commission_rate.is_primary',
      false,
    );

    if (isEmpty(commissionRateEntityId)) {
      setFormFieldValue(
        form,
        this.formFieldPath.commissionRateEntityId,
        commissionRateEntityId,
        false,
      );

      return observableOf(null);
    }

    // Populate commissionRates from snapshot so fetchList returns the rate,
    // ensuring items$ is non-empty when setFormFieldValue triggers setSelection.
    this.commissionRates = [
      {
        name: commissionRateName,
        entity_id: commissionRateEntityId,
        is_primary: commissionRateIsPrimary,
      },
    ];
    this.commissionRateDataSource.purge();
    void this.commissionRateDataSource
      .nextPage()
      .then(() => {
        setFormFieldValue(
          form,
          this.formFieldPath.commissionRateEntityId,
          commissionRateEntityId,
          false,
        );
        // Directly drive selectedItems so searchControl shows the display text.
        // Cannot rely on valueChanges since emitEvent is false above.
        this.commissionRateDataSource.setSelection([commissionRateEntityId]);
      })
      .catch(() => {});

    return observableOf(null);
  }

  public populateProductsFromDocument(form: FormGroup): void {
    const products = get(this.document, 'virtual.products', []);

    setFormFieldValue(form, this.formFieldPath.products, products, false);
    if (this.isClosed) {
      this.gridHeight$.next(`calc(100vh - ${this.generalWithInfoHeight})`);
      setFormFieldValue(
        form,
        this.formFieldPath.products,
        filter(products, { is_active: true }),
        false,
      );

      setFormFieldValue(
        form,
        this.formFieldPath.virtualInformationData,
        [get(this.document, 'information_data', DEFAULT_INFORMATION_DATA)],
        false,
      );
    }
  }

  public async populateCommissionRateForHawker(
    form: FormGroup,
    employeeId: string,
  ): Promise<ICommissionRate> {
    if (isEmpty(employeeId)) {
      return;
    }

    const hawkerProfileRes = await this.hawkerProfileService
      .fetchHawkerProfileList({ employeeIds: [employeeId] })
      .toPromise();

    const commissionRates = get(
      find(get(hawkerProfileRes, 'items', []), { employee_id: employeeId }),
      'commission_rates',
      [],
    );
    this.commissionRates = commissionRates;

    const commissionRateId = this.getFormFieldValue(
      this.formFieldPath.commissionRateEntityId,
    ) as string;

    if (isEmpty(commissionRates)) {
      const commissionRateControl = this.getFormFieldControl(
        this.formFieldPath.commissionRateEntityId,
      );

      commissionRateControl.setErrors({
        noAvailableCommissionRate: this.i18nService.translate(
          I18n.Forms.HawkerSessionForm.NoAvailableCommissionRate,
        ),
      });
      this.setNoAvailableCommissionRate(form);

      this.commissionRateDataSource.purge();
      void this.commissionRateDataSource.nextPage();

      return;
    }
    /**
     * default is primary commission rate if commissionRateId is not set or the commissionRateId cannot be found in the hawker profile's commission rates anymore
     */
    const selectedRate = isEmpty(commissionRateId)
      ? find(commissionRates, { is_primary: true })
      : find(commissionRates, { entity_id: commissionRateId });

    // Load items into the data source BEFORE setting the widget value, so the
    // entity_select widget can resolve the selected item for display.
    this.commissionRateDataSource.purge();
    await this.commissionRateDataSource.nextPage();

    setFormFieldValue(
      form,
      this.formFieldPath.commissionRateEntityId,
      selectedRate?.entity_id,
      this.isHawkerTouched,
    );

    this.commissionRateDataSource.setSelection([selectedRate.entity_id]);

    return selectedRate;
  }

  public async populateEventInfo(form: FormGroup): Promise<void> {
    if (isEmpty(this.selectedEventId)) {
      return;
    }

    const eventSource = this.isUseSnapshotData()
      ? this.document?.event
      : ((await this.xprtDataService
          .getEventDetailById(this.selectedEventId)
          .toPromise()) as IEvent);

    const eventName = get(eventSource, 'name', '');
    const eventDate = this.isUseSnapshotData()
      ? this.formatCanceledEventDate(eventSource)
      : formatEventStartAndEndDateTime(eventSource, {
          companyUtilService: this.companyService,
        });

    setFormFieldValue(
      form,
      this.formFieldPath.eventId,
      this.selectedEventId,
      false,
    );
    setFormFieldValue(form, this.formFieldPath.eventName, eventName, false);
    setFormFieldValue(form, this.formFieldPath.eventDate, eventDate, false);
  }

  private formatCanceledEventDate(
    eventSource: Record<string, unknown>,
  ): string {
    const startDateTime = get(eventSource, 'start_date_time', '') as string;
    const endDateTime = get(eventSource, 'end_date_time', '') as string;

    const format = TIME_FORMATS.L_LT;

    const formattedStartDateTime =
      this.companyService.convertUTCDateWithTimeZone(
        startDateTime,
        true,
        format,
      );
    const formattedEndDateTime = this.companyService.convertUTCDateWithTimeZone(
      endDateTime,
      true,
      format,
    );

    return `${formattedStartDateTime} ~ ${formattedEndDateTime}`;
  }

  public async populateSelectedSiteInfo(form: FormGroup): Promise<void> {
    if (isEmpty(this.selectedSiteId)) {
      return;
    }

    if (this.isUseSnapshotData()) {
      const canceledSiteName = `${get(this.document, 'site_name', '')} [${get(this.document, 'store_number', '')}]`;

      setFormFieldValue(
        form,
        this.formFieldPath.siteName,
        canceledSiteName,
        false,
      );

      return;
    }

    const sites = await this.xprtDataService
      .getAllSitesByFilter({
        filter: `_id eq in(${this.selectedSiteId})`,
        select: ['store_number', 'name', 'id', 'i18n'],
        top: 1,
      })
      .toPromise();

    const siteData = head(sites);
    const siteName = `${get(siteData, 'name', '')} [${get(siteData, 'store_number', '')}]`;

    setFormFieldValue(
      form,
      this.formFieldPath.siteId,
      this.selectedSiteId,
      false,
    );
    setFormFieldValue(form, this.formFieldPath.siteName, siteName, false);
  }

  /**
   * populate employee info by state
   *
   * @param form
   * @returns
   */
  public populateEmployeeInfo(form: FormGroup): void {
    if (isEmpty(this.initialEmployeeId) || !this.isNewMode()) {
      return;
    }

    setFormFieldValue(
      form,
      this.formFieldPath.employeeId,
      this.initialEmployeeId,
      true,
    );
  }

  public async fetchHawkerOptionsForSite(
    siteId: string,
  ): Promise<IApiListResponse<IBaseEntityType>> {
    const siteStatusRes = await this.bohDataService
      .getEmployeeSiteStatus(
        { siteIds: [siteId], itemsPerPage: MAX_PAGE_SIZE },
        { removeDuplicate: true },
      )
      .toPromise();

    const employeeIds = map(
      get(siteStatusRes, 'items', []),
      (status) => status.EmployeeId,
    );

    if (isEmpty(employeeIds)) {
      return Promise.resolve({
        items: [],
        page_size: 0,
        total: 0,
        page_count: 0,
        current_page: 0,
      });
    }

    const [employeeRes, hawkerSessionsRes] = await observableCombineLatest([
      this.bohDataService.getEmployeeByIds(employeeIds),
      this.hawkerSessionService.fetchListByEmployeeIds(
        employeeIds,
        {
          filter: `status eq in(${HAWKER_SESSION_STATUS_VALUE.Open})`,
          select: ['employee', 'status', 'site_id', 'event'],
          orderby: 'created_at desc',
          top: MAX_PAGE_SIZE,
        },
        {
          withEventInfo: true,
          withSiteInfo: true,
        },
      ),
    ])
      .pipe(rx.first())
      .toPromise();

    this.hawkerSessions = get(
      hawkerSessionsRes,
      'items',
      [],
    ) as IHawkerSession[];

    this.hawkerOptions = get(employeeRes, 'items', []) as IEmployee[];

    const hawkers = chain(this.hawkerOptions)
      .map((employee) => {
        const fullName = formatEmployeeName(employee, {
          showBadgeNumber: true,
        });

        return {
          name: fullName,
          entity_id: get(employee, 'EmployeeId', ''),
        };
      })
      .sortBy((employee) => toLower(employee.name))
      .value();

    return {
      items: hawkers,
      page_size: hawkers.length,
      total: hawkers.length,
      page_count: 1,
      current_page: 1,
    };
  }

  public fetchProductList(commissionRateId: string): Observable<IProduct[]> {
    const employeeId: string = this.getFormFieldValue(
      this.formFieldPath.employeeId,
    );
    const siteId: string = this.selectedSiteId;

    if (isEmpty(employeeId) || isEmpty(commissionRateId) || isEmpty(siteId)) {
      return observableOf([]);
    }

    return observableCombineLatest([
      this.productService.fetchCurrentList(
        {
          filter: 'is_active eq in(true)',
          select: ['name', 'product_id'],
        },
        [siteId],
      ),
      this.xdmCommissionRateService.fetchList({
        filter: `entity_id eq in(${commissionRateId})`,
      }),
      this.getFormReadyObs(this.formKeys.addProduct),
    ]).pipe(
      rx.switchMap(([productsRes, commissionRatesRes, _]) => {
        const allAssignedProducts = get(
          head(get(commissionRatesRes, 'items', [])),
          'commission_rates',
          [],
        );

        if (isEmpty(allAssignedProducts)) {
          return observableOf([]);
        }

        const products = get(productsRes, 'items', []);

        const assignedProductIds = map(
          allAssignedProducts,
          'product_entity_id',
        );
        const productIds = map(products, 'entity_id');

        const productIdIntersection = intersection(
          assignedProductIds,
          productIds,
        );

        if (isEmpty(productIdIntersection)) {
          return observableOf([]);
        }

        return this.hawkerLoadService
          .fetchList({
            select: ['products'],
          })
          .pipe(
            rx.map((hawkerLoadsRes) => {
              const hawkerLoads = get(
                head(get(hawkerLoadsRes, 'items', [])),
                'products',
                [],
              );

              return map(productIdIntersection, (productId) =>
                omit(
                  {
                    ...find(products, { entity_id: productId }),
                    load: get(
                      find(hawkerLoads, { entity_id: productId }),
                      'load',
                      DEFAULT_LOAD,
                    ),
                  },
                  ['is_active'],
                ),
              );
            }),
          );
      }),
    );
  }

  public updateRequestListWithSaved(params: {
    form: FormGroup;
    products: IProduct[];
    savedProducts: IProduct[];
  }): void {
    const { form, products, savedProducts } = params;

    const productsWithSaved = map(products, (product) => {
      const savedProduct = find(savedProducts, {
        entity_id: product.entity_id,
      });

      return {
        ...product,
        is_active: get(savedProduct, 'is_active'),
        total_requested: get(savedProduct, 'total_requested'),
        total_returned: get(savedProduct, 'total_returned'),
      };
    });

    const status = getFormFieldValue(form, this.formFieldPath.status);
    const isNewStatus = status === HAWKER_SESSION_STATUS_VALUE.New;

    const isIncluded = (product: IProduct): boolean => {
      if (isNewStatus) {
        return true;
      }

      const totalRequested = get(product, 'total_requested');
      const totalReturned = get(product, 'total_returned');
      const hasRequestedOrReturned =
        !isNil(totalRequested) || !isNil(totalReturned);

      return hasRequestedOrReturned && get(product, 'is_active', false);
    };

    const [formProducts, rightProducts] = partition(
      sortData(productsWithSaved, 'name', 'product_id'),
      isIncluded,
    );

    setFormFieldValue(form, this.formFieldPath.products, formProducts, false);

    setFormFieldValue(
      this.getForm(this.formKeys.addProduct),
      this.rightFormFieldPath.products,
      rightProducts,
      false,
    );
  }

  public getTotalNumberFormatter(): ColDef['valueFormatter'] {
    return (params: ValueFormatterParams) => {
      const value = params.value;

      return isNil(value)
        ? '-'
        : this.numberFormatService.getNumberFormatter(false)({
            value: params.value,
          });
    };
  }

  public onGridReadyHandler(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    this.xuxGrid = grid;
    this.gridApi = action?.payload?.gridApi;

    this.hiddenColumnsForSnapOrPermission(this.gridApi);

    if (this.isUseSnapshotData()) {
      if (this.isCanceled) {
        this.hideCanceledSessionColumns();
      } else {
        this.showClosedSessionColumns();
      }
    }
  }

  public onAddProductGridReadyHandler(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    this.addProductXuxGrid = grid;
    this.addProductGridApi = action?.payload?.gridApi;

    this.hiddenColumnsForSnapOrPermission(this.addProductGridApi);
  }

  public hiddenColumnsForSnapOrPermission(gridApi: GridApi): void {
    const columnApi: ColumnApi = gridApi?.['columnController']?.columnApi;
    const { New, Open } = HAWKER_SESSION_STATUS_VALUE;
    const currentStatus = this.getFormFieldValue(this.formFieldPath.status);
    const canEdit = this.canEdit && includes([New, Open], currentStatus);

    columnApi?.setColumnsVisible([ROW_SELECTOR_ID, 'add'], canEdit);
    this.isFormDisabled$.next(!canEdit);
  }

  public hideCanceledSessionColumns(): void {
    this.setSessionColumnsVisibility({
      columnsToHide: this.sessionColumnsToHide,
    });
  }

  public showClosedSessionColumns(): void {
    this.setSessionColumnsVisibility({
      columnsToHide: this.sessionColumnsToHide,
      columnsToShow: this.closedSessionColumnsToShow,
    });
  }

  private setSessionColumnsVisibility(options: {
    columnsToHide: string[];
    columnsToShow?: string[];
  }): void {
    const columnApi: ColumnApi = this.xuxGrid?.xuxGridComponent?.columnApi;
    const { columnsToHide, columnsToShow = [] } = options;

    columnApi?.setColumnsVisible(columnsToHide, false);

    if (!isEmpty(columnsToShow)) {
      columnApi?.setColumnsVisible(columnsToShow, true);
    }

    this.gridApi?.sizeColumnsToFit();
  }

  public getInlineChangeMapper(): (
    value: IProduct,
    payload: XuxGridActionPayload,
  ) => GridRowData {
    return (value: IProduct, _: XuxGridActionPayload): GridRowData => {
      return assignIn({}, value, {
        requested: calcQtyRequested(value),
      });
    };
  }

  public getReturnedQtyOverflowValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formStatus = this.form
        ? getFormFieldValue(this.form, this.formFieldPath.status)
        : get(control, ['root', 'value', this.formFieldPath.status]);

      if (
        includes(
          [
            HAWKER_SESSION_STATUS_VALUE.Closed,
            HAWKER_SESSION_STATUS_VALUE.Canceled,
          ],
          formStatus,
        )
      ) {
        return null;
      }

      const gridProductData: IProduct[] = control.value || [];

      if (isEmpty(gridProductData)) {
        return null;
      }

      const errorRows = chain(gridProductData)
        .filter((product) => {
          const returned = get(product, 'returned', 0);

          if (isNil(returned)) {
            return false;
          }

          return decimalNumber(get(product, 'requested', 0) ?? 0)
            .add(get(product, 'total_requested', 0) ?? 0)
            .sub(get(product, 'total_returned', 0) ?? 0)
            .lt(returned);
        })
        .map((product) => get(product, 'entity_id'))
        .value();

      return isEmpty(errorRows)
        ? null
        : {
            returnedQtyOverflow: {
              data: errorRows,
              enableDefaultValidationRowClass: true,
              message: I18n.ErrorMessages.QtyReturnedExceedsQtyRequested,
            },
          };
    };
  }

  public constructor(public injector: Injector) {
    super(injector);

    this.initHawkerIdDataSource();
    this.initCommissionRateDataSource();
  }

  public destroyFormDefinition(): void {
    super.destroyFormDefinition();

    // clear hawker filter & items, for next request as new hawkers
    this.lastFetchDataEmployeeId = null;
    this.hawkerIdDataSource?.refresh();
    this.commissionRateDataSource?.refresh();
    this.selectedCommissionRatePlaceholder$.next('');
    this.hawkerOptions = [];
    this.hawkerSessions = [];
    this.commissionRates = [];
    this.hawkerOptions$.next([]);
    this.shouldEmitEmployee = true;
    this.isRequestingProducts$.next(false);
    this.gridHeight$.next(this.defaultGridHeight);
  }

  public addProducts(
    editor: HawkerSessionEditContainerComponent,
    productEntityIds: string[],
  ): void {
    const form = editor.form;
    const rightForm = editor.getForm(this.formKeys.addProduct);
    const productsPath = this.formFieldPath.products;
    const rightProductsPath = this.rightFormFieldPath.products;

    const mainProducts: IProduct[] =
      getFormFieldValue(form, productsPath) || [];
    const rightProducts: IProduct[] = getFormFieldValue(
      rightForm,
      rightProductsPath,
    );

    const [productsToAdd, updatedRightProducts] = partition(
      rightProducts,
      (product) => includes(productEntityIds, get(product, 'entity_id')),
    );

    const updatedMainProducts = uniqBy(
      [
        ...mainProducts,
        ...map(productsToAdd, (p) => ({ ...p, is_active: true })),
      ],
      'entity_id',
    );

    setFormFieldValue(form, productsPath, updatedMainProducts);
    setFormFieldValue(
      rightForm,
      rightProductsPath,
      sortData(updatedRightProducts, 'name', 'product_id'),
      false,
    );
  }

  public removeProducts(
    editor: HawkerSessionEditContainerComponent,
    productEntityIds: string[],
  ): void {
    const form = editor.form;
    const rightForm = editor.getForm(this.formKeys.addProduct);
    const productsPath = this.formFieldPath.products;
    const rightProductsPath = this.rightFormFieldPath.products;

    const mainProducts: IProduct[] = getFormFieldValue(form, productsPath);
    const rightProducts: IProduct[] =
      getFormFieldValue(rightForm, rightProductsPath) || [];

    const [productsToRemove, updatedMainProducts] = partition(
      mainProducts,
      (product) => includes(productEntityIds, get(product, 'entity_id')),
    );
    const updatedRightProducts = uniqBy(
      [
        ...rightProducts,
        ...map(productsToRemove, (p) => ({ ...p, is_active: false })),
      ],
      'entity_id',
    );

    setFormFieldValue(form, productsPath, updatedMainProducts);
    setFormFieldValue(
      rightForm,
      rightProductsPath,
      sortData(updatedRightProducts, 'name', 'product_id'),
      false,
    );
  }

  public fetchProductsAndUpdateRequestList(
    form: FormGroup,
    commissionRateId: string,
  ): Observable<IProduct[]> {
    return this.fetchProductList(commissionRateId).pipe(
      rx.tap((products) =>
        this.updateRequestListWithSaved({
          form,
          products,
          savedProducts:
            (this.getFormFieldValue(
              this.formFieldPath.virtualProducts,
            ) as IProduct[]) || [],
        }),
      ),
    );
  }
}
